import { useState, useContext, createContext } from 'react';
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  fromPromise,
  from,
  HttpLink,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';

import {
  fetchCurrentUser,
  signInUser,
  signOutUser,
  signUpUser,
  verifySignUpUser,
  resetPassword,
  verifyResetPassword,
  resendVerificationToken,
  agreeToTerms,
} from '../API';

const APOLLO_CLIENT_URI = import.meta.env.VITE_APOLLO_CLIENT_URI;

const AuthContext = createContext();

let accessToken;

export const AuthAndApolloProvider = ({ children }) => {
  const [user, setUser] = useState();
  const [attemptingSilentRefresh, setAttemptingSilentRefresh] = useState(false);

  const httpLink = new HttpLink({
    uri: APOLLO_CLIENT_URI,
    credentials: 'include',
  });

  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors) {
        for (let error of graphQLErrors) {
          if (error.extensions?.code === 'UNAUTHENTICATED') {
            if (attemptingSilentRefresh) {
              return null;
            }

            return fromPromise(
              fetchCurrentUser()
                .then(({ refreshToken: refreshedUser }) => {
                  accessToken = refreshedUser.token;
                  return refreshedUser;
                })
                .catch(() => {
                  setUser(null);
                  return;
                })
            )
              .filter((value) => {
                return Boolean(value);
              })
              .flatMap(() => {
                return forward(operation);
              });
          }
        }
      }
      if (networkError) {
        try {
          JSON.parse(networkError.bodyText);
        } catch (e) {
          networkError.message =
            networkError.response?.statusText ||
            `Something went wrong on our end. Please contact support at ${
              import.meta.env.SUPPORT_EMAIL ?? 'support'
            }.`;
        }
      }
    }
  );

  const authLink = setContext((_, { headers }) => {
    return {
      headers: {
        ...headers,
        authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
    };
  });

  const client = new ApolloClient({
    link: from([errorLink, authLink, httpLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      mutate: {
        errorPolicy: 'all',
      },
    },
  });

  const getUser = async ({ force } = {}) => {
    if (user && !force) {
      return user;
    }

    setAttemptingSilentRefresh(true);
    try {
      const { refreshToken: user } = await fetchCurrentUser();
      accessToken = user.token;
      setUser(user);
    } catch (err) {
      console.log('Silent refresh failed');
    }
    setAttemptingSilentRefresh(false);
  };

  const updateUserPlan = async (plan) => {
    if (!user) {
      console.error('no user');
    }

    setUser({
      ...user,
      plan,
    });
  };

  const signup = async ({ email, password, companyName, agreeToTerms }) => {
    try {
      const { signUpUser: signedUpUser } = await signUpUser(
        email,
        password,
        companyName,
        agreeToTerms
      );
      return signedUpUser.email;
    } catch (err) {
      throw new Error(err);
    }
  };

  const verifysignup = async (verificationToken) => {
    try {
      const { verifySignUpUser: verifiedUser } = await verifySignUpUser(
        verificationToken
      );
      return { email: verifiedUser.email, verified: verifiedUser.verified };
    } catch (err) {
      throw new Error(err);
    }
  };

  const agreetoterms = async ({ email, agreeToTerms: agreed }) => {
    try {
      const { agreeToTerms: user } = await agreeToTerms(email, agreed);
      return { email: user.email };
    } catch (err) {
      throw new Error(err);
    }
  };

  const signin = async ({ email, password }) => {
    try {
      const { signInUser: fetchedUser } = await signInUser(email, password);
      accessToken = fetchedUser.token;
      setUser(fetchedUser);
    } catch (err) {
      throw new Error(err);
    }
  };

  const signout = async () => {
    try {
      await signOutUser(user.id);
    } catch (err) {
      console.error('Failed to revoke tokens.');
    }
    setUser(null);
    accessToken = null;
    client.clearStore();
    Object.keys(localStorage)
      .filter((key) => key.startsWith('atad-')) // TODO: make this an env var
      .map((key) => localStorage.removeItem(key));
  };

  const resetpassword = async (email) => {
    try {
      await resetPassword(email);
    } catch (err) {
      throw new Error(err);
    }
  };

  const verifyresetpassword = async (resetToken, password) => {
    try {
      const { verifyResetPassword: resetUser } = await verifyResetPassword(
        resetToken,
        password
      );
      return resetUser.email;
    } catch (err) {
      throw new Error(err);
    }
  };

  const resendverificationtoken = async (email) => {
    try {
      const { sendNewVerificationToken: signedUpUser } =
        await resendVerificationToken(email);
      return signedUpUser.email;
    } catch (err) {
      throw new Error(err);
    }
  };

  let auth = {
    user,
    setUser,
    getUser,
    updateUserPlan,
    attemptingSilentRefresh,
    signup,
    signin,
    signout,
    verifysignup,
    resetpassword,
    verifyresetpassword,
    resendverificationtoken,
    agreetoterms,
  };

  return (
    <AuthContext.Provider value={auth}>
      <ApolloProvider client={client}>{children}</ApolloProvider>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
